import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Btn } from '../actions';
import { IconClose } from '../icons';
import { findExactSearchSuggestOption } from './searchSuggestMatch';
import { useBlockBrowserAutofill } from './useBlockBrowserAutofill';

/** Match `.search-suggest-dropdown` max-height in ui-components.css */
const SEARCH_SUGGEST_DROPDOWN_MAX_HEIGHT = 220;

function getSearchSuggestPlacement(input: HTMLElement): {
    openUpward: boolean;
    maxHeight: number;
    style: CSSProperties;
} {
    const inputRect = input.getBoundingClientRect();
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const openUpward = spaceBelow < SEARCH_SUGGEST_DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
    const available = (openUpward ? spaceAbove : spaceBelow) - 8;
    const maxHeight = Math.max(80, Math.min(SEARCH_SUGGEST_DROPDOWN_MAX_HEIGHT, available));
    return {
        openUpward,
        maxHeight,
        style: {
            position: 'fixed',
            left: inputRect.left,
            right: 'auto',
            width: inputRect.width,
            // Above Resources dialogs (.cr-modal-overlay is 3000). Below the
            // leave-page blocker and the navigation drawer.
            zIndex: 4000,
            maxHeight,
            ...(openUpward
                ? { top: 'auto', bottom: window.innerHeight - inputRect.top + 2 }
                : { top: inputRect.bottom + 2, bottom: 'auto' }),
        },
    };
}

export interface SearchSuggestOption {
    value: string;
    label: string;
}

export interface SearchSuggestFooterAction {
    label: string;
    onClick: () => void;
}

export interface SearchSuggestActionOption {
    label: string;
    onClick: (context: { searchText: string }) => void;
    /** When true, only show the action in the typed add row (requires non-matching search text). */
    typedOnly?: boolean;
}

export interface SearchSuggestProps {
    options: SearchSuggestOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** Shown as placeholder when `disabled` is true (e.g. prerequisite hint). Also announced via aria-describedby. */
    disabledMessage?: string;
    disabled?: boolean;
    hasError?: boolean;
    emptyMessage?: string;
    allowNew?: boolean;
    newLabel?: string;
    resolveValue?: (text: string) => string;
    formatDisplayValue?: (value: string) => string;
    actionOption?: SearchSuggestActionOption;
    focusInputOnFooterAction?: boolean;
    footerAction?: SearchSuggestFooterAction;
    className?: string;
    inputId?: string;
    ariaLabel?: string;
    onBlur?: () => void;
    onInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    ariaDescribedBy?: string;
    /** When true, shows a clear (close) control when the field has a value or typed text. */
    clearable?: boolean;
    /** Fired when a typed (non-option) value is committed via add-row, Enter, or blur. */
    onCommitNew?: (text: string, resolvedValue: string) => void;
}

export const SearchSuggest = ({
    options,
    value,
    onChange,
    placeholder = 'Type to search...',
    disabledMessage,
    disabled = false,
    hasError = false,
    emptyMessage = 'No matches found',
    allowNew = false,
    newLabel = '+ New',
    resolveValue: resolveValueProp,
    formatDisplayValue,
    actionOption,
    focusInputOnFooterAction = false,
    footerAction,
    className,
    inputId,
    ariaLabel,
    onBlur,
    onInputKeyDown,
    ariaDescribedBy,
    clearable = false,
    onCommitNew,
}: SearchSuggestProps) => {
    const generatedId = useId();
    const resolvedInputId = inputId || generatedId;
    const listboxId = `${resolvedInputId}-listbox`;
    const [openUpward, setOpenUpward] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

    const defaultResolveValue = (text: string): string => {
        const trimmed = text.trim();
        if (!trimmed) return '';
        const exactMatch = findExactSearchSuggestOption(options, trimmed);
        return exactMatch ? exactMatch.value : trimmed;
    };

    const effectiveResolveValue = resolveValueProp ?? defaultResolveValue;

    const selectedLabel = useMemo(() => {
        // Keep the input empty for unselected values so the placeholder shows
        // and we do not treat "— None —" (value "") as an active filter query.
        if (!value) return '';
        const optionLabel = options.find((option) => option.value === value)?.label;
        if (optionLabel) return optionLabel;
        if (formatDisplayValue) return formatDisplayValue(value);
        return value;
    }, [formatDisplayValue, options, value]);

    const [searchText, setSearchText] = useState(selectedLabel);
    const [isOpen, setIsOpen] = useState(false);
    const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
    const { unlockAutofillGuard, autofillInputProps } = useBlockBrowserAutofill(disabled);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const displayLabelForValue = (resolvedValue: string) =>
        options.find((option) => option.value === resolvedValue)?.label ||
        (formatDisplayValue ? formatDisplayValue(resolvedValue) : resolvedValue);

    const commitIfNewValue = (text: string, resolved: string) => {
        if (!resolved) return;
        if (options.some((option) => option.value === resolved)) return;
        onCommitNew?.(text.trim(), resolved);
    };

    const closeDropdown = (commit = false) => {
        setIsOpen(false);
        setActiveOptionIndex(-1);
        if (commit && allowNew) {
            const resolved = effectiveResolveValue(searchText);
            if (resolved !== value) onChange(resolved);
            commitIfNewValue(searchText, resolved);
            setSearchText(displayLabelForValue(resolved));
            return;
        }
        if (commit && !allowNew) {
            const exact = findExactSearchSuggestOption(options, searchText);
            if (exact) {
                if (exact.value !== value) onChange(exact.value);
                setSearchText(exact.label);
                return;
            }
        }
        setSearchText(selectedLabel);
    };

    const selectOption = (option: SearchSuggestOption) => {
        onChange(option.value);
        setSearchText(option.label);
        setIsOpen(false);
        setActiveOptionIndex(-1);
    };

    const commitNewValue = (text: string) => {
        const resolved = effectiveResolveValue(text);
        onChange(resolved);
        commitIfNewValue(text, resolved);
        setSearchText(displayLabelForValue(resolved));
        setIsOpen(false);
        setActiveOptionIndex(-1);
    };

    useEffect(() => {
        setSearchText(selectedLabel);
    }, [selectedLabel]);

    // Only filter after the user edits the input. Matching the selected label
    // (e.g. opening the list with "Conveyors" selected) must show the full list.
    // With allowNew, live onChange keeps value === searchText for free-text queries,
    // so also treat "value is not an option id" as actively filtering.
    const isActivelyFiltering = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) return false;
        if (query !== selectedLabel.trim().toLowerCase()) return true;
        if (!allowNew) return false;
        return !options.some((option) => option.value === value);
    }, [allowNew, options, searchText, selectedLabel, value]);

    const filteredOptions = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query || !isActivelyFiltering) return options;
        return options.filter((option) => option.label.toLowerCase().includes(query));
    }, [isActivelyFiltering, options, searchText]);

    const trimmedSearch = searchText.trim();

    const hasExactLabelMatch = useMemo(() => {
        if (!trimmedSearch) return false;
        const query = trimmedSearch.toLowerCase();
        return options.some((option) => option.label.toLowerCase() === query);
    }, [options, trimmedSearch]);

    const showAllowNewTypedRow = useMemo(() => {
        if (!allowNew || !trimmedSearch) return false;
        const resolved = effectiveResolveValue(searchText);
        return !options.some((option) => option.value === resolved);
    }, [allowNew, effectiveResolveValue, options, searchText, trimmedSearch]);

    const showActionTypedRow = Boolean(
        actionOption && trimmedSearch && !hasExactLabelMatch,
    );

    const showTypedAddRow = showAllowNewTypedRow || showActionTypedRow;

    const showPersistentActionRow = Boolean(
        actionOption && !showTypedAddRow && !actionOption.typedOnly,
    );

    const typedAddLabel =
        showAllowNewTypedRow ? newLabel : actionOption?.label;

    const typedAddDisplay = showAllowNewTypedRow
        ? displayLabelForValue(effectiveResolveValue(searchText))
        : trimmedSearch;

    const handleTypedAddSelect = () => {
        if (showAllowNewTypedRow) {
            commitNewValue(searchText);
            return;
        }
        actionOption?.onClick({ searchText: trimmedSearch });
        setIsOpen(false);
        setActiveOptionIndex(-1);
    };

    useEffect(() => {
        if (!isOpen) {
            setActiveOptionIndex(-1);
            return;
        }
        if (isActivelyFiltering) {
            const exact = findExactSearchSuggestOption(filteredOptions, searchText);
            setActiveOptionIndex(
                exact ? filteredOptions.findIndex((option) => option.value === exact.value) : -1,
            );
            return;
        }
        setActiveOptionIndex(filteredOptions.findIndex((option) => option.value === value));
    }, [filteredOptions, isActivelyFiltering, isOpen, searchText, value]);

    useEffect(() => {
        if (activeOptionIndex < 0 || !dropdownRef.current) return;
        const activeEl = dropdownRef.current.querySelector(`[data-option-index="${activeOptionIndex}"]`);
        activeEl?.scrollIntoView({ block: 'nearest' });
    }, [activeOptionIndex]);

    useLayoutEffect(() => {
        if (!isOpen || disabled) {
            setOpenUpward(false);
            return;
        }

        const updatePlacement = (event?: Event) => {
            if (event && dropdownRef.current?.contains(event.target as Node)) return;
            const input = inputRef.current;
            if (!input) return;
            const next = getSearchSuggestPlacement(input);
            setOpenUpward((prev) => (prev === next.openUpward ? prev : next.openUpward));
            setDropdownStyle(next.style);
        };

        updatePlacement();
        window.addEventListener('resize', updatePlacement);
        document.addEventListener('scroll', updatePlacement, true);
        return () => {
            window.removeEventListener('resize', updatePlacement);
            document.removeEventListener('scroll', updatePlacement, true);
        };
    }, [disabled, isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current?.contains(event.target as Node) ||
                inputRef.current?.contains(event.target as Node)
            ) {
                return;
            }

            closeDropdown(true);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [allowNew, selectedLabel, searchText, value]);

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) {
            if (event.key !== 'Tab') {
                event.preventDefault();
            }
            return;
        }

        if (event.key === 'Tab') {
            const exact = findExactSearchSuggestOption(options, searchText);
            if (exact && exact.value !== value) onChange(exact.value);
        }

        onInputKeyDown?.(event);

        if (event.key === 'Escape') {
            closeDropdown(false);
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (disabled) return;

            setIsOpen(true);
            if (filteredOptions.length === 0) return;

            setActiveOptionIndex((current) => {
                if (!isOpen || current < 0) return 0;
                return (current + 1) % filteredOptions.length;
            });
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (disabled || filteredOptions.length === 0) return;

            setIsOpen(true);
            setActiveOptionIndex((current) => {
                if (!isOpen || current < 0) return filteredOptions.length - 1;
                return current <= 0 ? filteredOptions.length - 1 : current - 1;
            });
            return;
        }

        if (event.key === 'Enter') {
            if (!isOpen) return;
            if (activeOptionIndex >= 0) {
                const option = filteredOptions[activeOptionIndex];
                if (!option) return;
                event.preventDefault();
                selectOption(option);
                return;
            }
            const exact = findExactSearchSuggestOption(filteredOptions, searchText);
            if (exact) {
                event.preventDefault();
                selectOption(exact);
                return;
            }
            if (showTypedAddRow) {
                event.preventDefault();
                handleTypedAddSelect();
            }
        }
    };

    const handleInputBlur = () => {
        closeDropdown(true);
        onBlur?.();
    };

    const handleClear = () => {
        onChange('');
        setSearchText('');
        setIsOpen(false);
        setActiveOptionIndex(-1);
        inputRef.current?.focus();
    };

    const showClear = clearable && !disabled && Boolean(value || searchText.trim());
    const rootClassName = ['search-suggest', showClear ? 'has-clear' : '', className]
        .filter(Boolean)
        .join(' ');
    const inputClassName = ['search-suggest-input', hasError ? 'has-error' : ''].filter(Boolean).join(' ');
    const resolvedPlaceholder = disabled ? (disabledMessage || placeholder) : placeholder;
    const disabledHintId = disabled && disabledMessage ? `${resolvedInputId}-disabled-hint` : undefined;
    const describedBy = [ariaDescribedBy, disabledHintId].filter(Boolean).join(' ') || undefined;

    return (
        <div className={rootClassName}>
            <input
                ref={inputRef}
                id={resolvedInputId}
                className={inputClassName}
                value={searchText}
                placeholder={resolvedPlaceholder}
                aria-disabled={disabled || undefined}
                aria-label={ariaLabel}
                aria-describedby={describedBy}
                aria-invalid={hasError || undefined}
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                    isOpen && activeOptionIndex >= 0
                        ? `${resolvedInputId}-option-${activeOptionIndex}`
                        : undefined
                }
                role="combobox"
                {...autofillInputProps}
                onChange={(event) => {
                    if (disabled) return;
                    const text = event.target.value;
                    setSearchText(text);
                    setIsOpen(true);
                    if (allowNew) onChange(effectiveResolveValue(text));
                }}
                onFocus={(event) => {
                    if (disabled) return;
                    unlockAutofillGuard();
                    setIsOpen(true);
                    // Select current label so typing replaces it instead of appending.
                    event.currentTarget.select();
                }}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
            />
            {disabledHintId && disabledMessage ? (
                <span id={disabledHintId} className="search-suggest-disabled-hint">
                    {disabledMessage}
                </span>
            ) : null}
            {showClear ? (
                <span className="search-suggest-clear-wrap" onMouseDown={(event) => event.preventDefault()}>
                    <Btn
                        variant="icon"
                        buttonSize="sm"
                        buttonClasses="search-suggest-clear"
                        buttonIcon={<IconClose iconClasses="search-suggest-clear-icon" />}
                        ariaLabel="Clear search"
                        buttonOnClick={handleClear}
                    />
                </span>
            ) : null}
            {isOpen && !disabled && createPortal(
                <div
                    ref={dropdownRef}
                    id={listboxId}
                    className={['search-suggest-dropdown', openUpward ? 'is-up' : ''].filter(Boolean).join(' ')}
                    role="listbox"
                    style={dropdownStyle}
                    onMouseDown={(event) => event.preventDefault()}
                    onWheel={(event) => event.stopPropagation()}
                >
                    {filteredOptions.map((option, index) => (
                        <button
                            key={option.value}
                            id={`${resolvedInputId}-option-${index}`}
                            data-option-index={index}
                            type="button"
                            role="option"
                            aria-selected={value === option.value}
                            className={[
                                'search-suggest-option',
                                value === option.value ? 'is-selected' : '',
                                index === activeOptionIndex ? 'is-active' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onMouseDown={(event) => event.preventDefault()}
                            onMouseEnter={() => setActiveOptionIndex(index)}
                            onClick={() => selectOption(option)}
                        >
                            {option.label}
                        </button>
                    ))}
                    {trimmedSearch &&
                        filteredOptions.length === 0 &&
                        !showTypedAddRow &&
                        !showPersistentActionRow && (
                        <div className="search-suggest-option is-empty">{emptyMessage}</div>
                    )}
                    {showTypedAddRow && typedAddLabel && (
                        <button
                            type="button"
                            role="option"
                            className="search-suggest-option search-suggest-option--new"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={handleTypedAddSelect}
                        >
                            {typedAddLabel}: &ldquo;{typedAddDisplay}&rdquo;
                        </button>
                    )}
                    {showPersistentActionRow && actionOption && (
                        <button
                            type="button"
                            role="option"
                            className="search-suggest-option search-suggest-option--new"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                                actionOption.onClick({ searchText: trimmedSearch });
                                setIsOpen(false);
                                setActiveOptionIndex(-1);
                            }}
                        >
                            {actionOption.label}
                        </button>
                    )}
                    {footerAction && (
                        <div
                            className="search-suggest-footer-action"
                            onMouseDown={(event) => event.preventDefault()}
                        >
                            <Btn
                                variant="secondary"
                                buttonSize="sm"
                                buttonText={footerAction.label}
                                buttonOnClick={() => {
                                    footerAction.onClick();
                                    if (focusInputOnFooterAction) {
                                        setIsOpen(true);
                                        inputRef.current?.focus();
                                    } else {
                                        setIsOpen(false);
                                    }
                                }}
                                buttonClasses="search-suggest-footer-btn"
                            />
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
};
